import type { UploadedFile } from '~/types/models';

export type ZipArchiveEntry = {
    name: string;
    data: Uint8Array;
};

export type ZipDownloadProgress = {
    phase: 'downloading' | 'finalising';
    completed: number;
    total: number;
    fileName?: string;
};

export type ZipFileResponseLoader = (file: UploadedFile) => Promise<Response>;

const crcTable = new Uint32Array(256).map((_, index) => {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
        value = (value & 1) ? (0xedb88320 ^ (value >>> 1)) : (value >>> 1);
    }
    return value >>> 0;
});

function crc32(data: Uint8Array): number {
    let crc = 0xffffffff;
    for (const byte of data) crc = crcTable[(crc ^ byte) & 0xff]! ^ (crc >>> 8);
    return (crc ^ 0xffffffff) >>> 0;
}

function dosDateTime(date = new Date()) {
    const year = Math.max(1980, date.getFullYear());
    return {
        time: (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2),
        date: ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
    };
}

function writeUint16(view: DataView, offset: number, value: number) {
    view.setUint16(offset, value, true);
}

function writeUint32(view: DataView, offset: number, value: number) {
    view.setUint32(offset, value >>> 0, true);
}

function joinChunks(chunks: Uint8Array[]): Uint8Array {
    const output = new Uint8Array(chunks.reduce((total, chunk) => total + chunk.byteLength, 0));
    let offset = 0;
    for (const chunk of chunks) {
        output.set(chunk, offset);
        offset += chunk.byteLength;
    }
    return output;
}

function uniqueArchiveNames(entries: ZipArchiveEntry[]): ZipArchiveEntry[] {
    const used = new Set<string>();
    return entries.map((entry) => {
        const normalized = String(entry.name || 'file').split(/[\\/]/).pop()?.replace(/[\u0000-\u001f]/g, '') || 'file';
        const dot = normalized.lastIndexOf('.');
        const stem = dot > 0 ? normalized.slice(0, dot) : normalized;
        const extension = dot > 0 ? normalized.slice(dot) : '';
        let name = normalized;
        let suffix = 2;
        while (used.has(name.toLowerCase())) name = `${stem} (${suffix++})${extension}`;
        used.add(name.toLowerCase());
        return { ...entry, name };
    });
}

export function createStoredZip(entries: ZipArchiveEntry[], createdAt = new Date()): Uint8Array {
    if (entries.length === 0) throw new Error('Choose at least one file to download.');
    if (entries.length > 0xffff) throw new Error('Too many files were selected for one ZIP archive.');

    const encoder = new TextEncoder();
    const localChunks: Uint8Array[] = [];
    const centralChunks: Uint8Array[] = [];
    const { time, date } = dosDateTime(createdAt);
    let localOffset = 0;

    for (const entry of uniqueArchiveNames(entries)) {
        const name = encoder.encode(entry.name);
        const checksum = crc32(entry.data);
        const localHeader = new Uint8Array(30 + name.length);
        const localView = new DataView(localHeader.buffer);
        writeUint32(localView, 0, 0x04034b50);
        writeUint16(localView, 4, 20);
        writeUint16(localView, 6, 0x0800);
        writeUint16(localView, 8, 0);
        writeUint16(localView, 10, time);
        writeUint16(localView, 12, date);
        writeUint32(localView, 14, checksum);
        writeUint32(localView, 18, entry.data.byteLength);
        writeUint32(localView, 22, entry.data.byteLength);
        writeUint16(localView, 26, name.length);
        writeUint16(localView, 28, 0);
        localHeader.set(name, 30);
        localChunks.push(localHeader, entry.data);

        const centralHeader = new Uint8Array(46 + name.length);
        const centralView = new DataView(centralHeader.buffer);
        writeUint32(centralView, 0, 0x02014b50);
        writeUint16(centralView, 4, 20);
        writeUint16(centralView, 6, 20);
        writeUint16(centralView, 8, 0x0800);
        writeUint16(centralView, 10, 0);
        writeUint16(centralView, 12, time);
        writeUint16(centralView, 14, date);
        writeUint32(centralView, 16, checksum);
        writeUint32(centralView, 20, entry.data.byteLength);
        writeUint32(centralView, 24, entry.data.byteLength);
        writeUint16(centralView, 28, name.length);
        writeUint32(centralView, 42, localOffset);
        centralHeader.set(name, 46);
        centralChunks.push(centralHeader);
        localOffset += localHeader.byteLength + entry.data.byteLength;
    }

    const centralDirectory = joinChunks(centralChunks);
    const end = new Uint8Array(22);
    const endView = new DataView(end.buffer);
    writeUint32(endView, 0, 0x06054b50);
    writeUint16(endView, 8, entries.length);
    writeUint16(endView, 10, entries.length);
    writeUint32(endView, 12, centralDirectory.byteLength);
    writeUint32(endView, 16, localOffset);

    return joinChunks([...localChunks, centralDirectory, end]);
}

function normalizeArchiveName(name: string): string {
    return String(name || 'file').split(/[\\/]/).pop()?.replace(/[\u0000-\u001f]/g, '') || 'file';
}

function uniqueFileNames(files: UploadedFile[]): string[] {
    const used = new Set<string>();
    return files.map((file) => {
        const normalized = normalizeArchiveName(file.fileName);
        const dot = normalized.lastIndexOf('.');
        const stem = dot > 0 ? normalized.slice(0, dot) : normalized;
        const extension = dot > 0 ? normalized.slice(dot) : '';
        let name = normalized;
        let suffix = 2;
        while (used.has(name.toLowerCase())) name = `${stem} (${suffix++})${extension}`;
        used.add(name.toLowerCase());
        return name;
    });
}

function startBlobDownload(blob: Blob, archiveName: string) {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = archiveName.toLowerCase().endsWith('.zip') ? archiveName : `${archiveName}.zip`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    window.setTimeout(() => {
        link.remove();
        URL.revokeObjectURL(objectUrl);
    }, 60_000);
}

type ZipFileHandle = {
    createWritable: () => Promise<WritableStream<Uint8Array> & {
        write: (data: Blob) => Promise<void>;
        close: () => Promise<void>;
    }>;
};

async function chooseZipDestination(archiveName: string): Promise<ZipFileHandle | null | undefined> {
    const picker = (window as Window & {
        showSaveFilePicker?: (options: {
            suggestedName: string;
            types: Array<{ description: string; accept: Record<string, string[]> }>;
        }) => Promise<ZipFileHandle>;
    }).showSaveFilePicker;
    if (!picker) return undefined;

    try {
        return await picker.call(window, {
            suggestedName: archiveName,
            types: [{
                description: 'ZIP archive',
                accept: { 'application/zip': ['.zip'] },
            }],
        });
    } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return null;
        throw error;
    }
}

export async function downloadUploadedFilesAsZip(
    files: UploadedFile[],
    archiveName = 'uploads.zip',
    onProgress?: (progress: ZipDownloadProgress) => void,
    loadFileResponse: ZipFileResponseLoader = file => fetch(file.fileURL),
): Promise<boolean> {
    if (!files.length) throw new Error('Choose at least one file to download.');
    if (!import.meta.client) throw new Error('ZIP downloads are only available in the browser.');

    const resolvedArchiveName = archiveName.toLowerCase().endsWith('.zip') ? archiveName : `${archiveName}.zip`;
    // Invoke the picker before the first network await so Chromium still considers
    // it part of the user's original click. Delayed synthetic link clicks are blocked.
    const destination = await chooseZipDestination(resolvedArchiveName);
    if (destination === null) return false;

    const { downloadZip } = await import('client-zip');
    const names = uniqueFileNames(files);

    async function* zipInputs() {
        for (let index = 0; index < files.length; index += 1) {
            const file = files[index]!;
            onProgress?.({
                phase: 'downloading',
                completed: index,
                total: files.length,
                fileName: file.fileName,
            });
            const response = await loadFileResponse(file);
            if (!response.ok) throw new Error(`Could not download ${file.fileName}.`);
            yield { name: names[index]!, input: response };
        }
    }

    const archive = downloadZip(zipInputs());
    if (destination) {
        const writable = await destination.createWritable();
        if (!archive.body) throw new Error('Unable to create the archive stream.');
        await archive.body.pipeTo(writable);
    } else {
        const blob = await archive.blob();
        startBlobDownload(blob, resolvedArchiveName);
    }
    onProgress?.({ phase: 'finalising', completed: files.length, total: files.length });
    return true;
}
